import { useCallback, useState } from "react";

/**
 * A setting the room remembers for next time, in this browser only.
 *
 * A therapist holds six sessions a day from the same desk and should not
 * re-pick their background and their layout for every one of them. Nothing
 * here is sent anywhere — it is a site setting, the same as a theme.
 *
 * Every access is guarded. `localStorage` throws outright in a private window
 * and in browsers set to block site data, and a preference that cannot be
 * remembered is never a reason to refuse the choice itself.
 */
export function useRememberedChoice<T extends string>(
    key: string,
    isValid: (value: unknown) => value is T,
    fallback: T,
    /** False where the choice cannot be honoured at all, so it is not restored. */
    enabled = true
): readonly [T, (value: T) => void] {
    const [choice, setChoice] = useState<T>(() => {
        if (!enabled) return fallback;
        try {
            const stored = window.localStorage.getItem(key);
            return isValid(stored) ? stored : fallback;
        } catch {
            return fallback;
        }
    });

    const choose = useCallback(
        (value: T) => {
            setChoice(value);
            try {
                window.localStorage.setItem(key, value);
            } catch {
                // Nothing to do, and nothing worth telling anyone about.
            }
        },
        [key]
    );

    return [choice, choose] as const;
}
